import Box from "@mui/material/Box";
import { GridFilterModel } from "@mui/x-data-grid-pro";
import { Dispatch, SetStateAction, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import StatusCard from "./StatusCard";
import { PageType } from "@/constants/PageType";
import useFilters from "@/pages/diagnosticmonitor/hooks/useFilters";

interface Props {
  drivingDevices: number;
  chargingDevices: number;
  notChargingDevices: number;
  chargeInterruptDevices: number;
  withStatusDevices: number;
  withPMDevices: number;
  fullWidth?: boolean;
  filterModel: GridFilterModel;
  setFilterModel: Dispatch<SetStateAction<GridFilterModel>>;
  pageType: PageType;
  activeAlertCount?: number;
  violatedDeviceNames?: string[];
}

const cardWrapperSx = {
  flex: "1 1 0",
  minWidth: 110,
  cursor: "pointer",
};

export default function DeviceFilterGrid(props: Props) {
  const { t } = useTranslation();

  const {
    drivingDevices,
    chargingDevices,
    notChargingDevices,
    chargeInterruptDevices,
    withPMDevices,
    withStatusDevices,
    filterModel,
    setFilterModel,
    pageType,
    activeAlertCount = 0,
    violatedDeviceNames,
  } = props;

  const { toggleFilter, isFilterActive } = useFilters(filterModel, setFilterModel);
  const showAlertCard = pageType === "diagnosticMonitor";
  const sortedAlertNames = useMemo(
    () => (violatedDeviceNames ? [...violatedDeviceNames].sort() : []),
    [violatedDeviceNames],
  );
  const ACTIVE_ALERT_FILTER_ID = "diagnosticMonitor:activeAlerts";
  const isActiveAlertFilter = filterModel.items.some(f => f.id === ACTIVE_ALERT_FILTER_ID);

  useEffect(() => {
    if (!showAlertCard || !isActiveAlertFilter) return;
    const idx = filterModel.items.findIndex(f => f.id === ACTIVE_ALERT_FILTER_ID);
    if (idx < 0) return;
    const current = filterModel.items[idx].value as string[] | undefined;
    if (JSON.stringify(current ?? []) === JSON.stringify(sortedAlertNames)) return;
    const updated = [...filterModel.items];
    updated[idx] = { ...updated[idx], value: sortedAlertNames };
    setFilterModel({ ...filterModel, items: updated });
  }, [sortedAlertNames, filterModel, setFilterModel, showAlertCard, isActiveAlertFilter]);

  const handleAlertCardClick = () => {
    if (isActiveAlertFilter) {
      setFilterModel({
        ...filterModel,
        items: filterModel.items.filter(f => f.id !== ACTIVE_ALERT_FILTER_ID),
      });
      return;
    }
    if (activeAlertCount === 0) return;
    setFilterModel({
      ...filterModel,
      items: [
        ...filterModel.items,
        {
          id: ACTIVE_ALERT_FILTER_ID,
          field: "device.name",
          operator: "isAnyOf",
          value: sortedAlertNames,
        },
      ],
    });
  };

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, width: "100%" }}>
      <Box
        sx={cardWrapperSx}
        onClick={() => toggleFilter({ field: "isDriving", operator: "is", value: "true" })}
      >
        <StatusCard
          label={t("faultOverview.driving")}
          value={drivingDevices}
          enabled={isFilterActive({ field: "isDriving", operator: "is", value: "true" })}
        />
      </Box>

      <Box
        sx={cardWrapperSx}
        onClick={() => toggleFilter({ field: "isCharging", operator: "isAnyOf", value: [1, 2] })}
      >
        <StatusCard
          label={t("faultOverview.charging")}
          value={chargingDevices}
          enabled={isFilterActive({ field: "isCharging", operator: "isAnyOf", value: [1, 2] })}
        />
      </Box>

      <Box
        sx={cardWrapperSx}
        onClick={() => toggleFilter({ field: "isCharging", operator: "is", value: 0 })}
      >
        <StatusCard
          label={t("faultOverview.notCharging")}
          value={notChargingDevices}
          enabled={isFilterActive({ field: "isCharging", operator: "is", value: 0 })}
        />
      </Box>

      <Box
        sx={cardWrapperSx}
        onClick={() => toggleFilter({ field: "isCharging", operator: "is", value: 3 })}
      >
        <StatusCard
          label={t("faultOverview.chargeInterrupt")}
          value={chargeInterruptDevices}
          highlight
          enabled={isFilterActive({ field: "isCharging", operator: "is", value: 3 })}
        />
      </Box>
      {showAlertCard && (
        <Box sx={cardWrapperSx} onClick={handleAlertCardClick}>
          <StatusCard
            label={t("faultOverview.activeAlerts")}
            value={activeAlertCount}
            highlight
            enabled={isActiveAlertFilter}
          />
        </Box>
      )}
      {pageType === "diagnosticMonitor" && (
        <>
          <Box
            sx={cardWrapperSx}
            onClick={() =>
              toggleFilter({
                field: "warnings",
                operator: "multiWarningFilter",
                id: 36350,
                value: ["predictiveMaintenance", "predictiveMaintenance1"],
              })
            }
          >
            <StatusCard
              label={t("faultOverview.withPM")}
              value={withPMDevices}
              enabled={isFilterActive({
                field: "warnings",
                operator: "multiWarningFilter",
                id: 36350,
                value: ["predictiveMaintenance", "predictiveMaintenance1"],
              })}
            />
          </Box>
          <Box
            sx={cardWrapperSx}
            onClick={() =>
              toggleFilter({
                field: "vehicleStatus",
                operator: "not",
                id: 52747,
                value: "__NONE__",
              })
            }
          >
            <StatusCard
              label={t("faultOverview.withStatus")}
              value={withStatusDevices}
              enabled={isFilterActive({
                field: "vehicleStatus",
                operator: "not",
                id: 52747,
                value: "__NONE__",
              })}
            />
          </Box>
        </>
      )}
    </Box>
  );
}
